import React, { useEffect, useRef } from "react";
import { useMap } from "@vis.gl/react-google-maps/dist/index.umd.js";
import type { TerraDrawEventListeners } from "terra-draw";
import { TerraDraw, TerraDrawPolyLineMode } from "terra-draw";
import { TerraDrawGoogleMapsAdapter } from "terra-draw-google-maps-adapter";
import {
  createSyntheticPolygon,
  isValidFinishedGeometry,
  mapPolygonStyles,
  type DrawingManagerOptions,
  type DrawSnapshotFeatureId,
  type GooglePolygonLike,
} from "./MapaGoogleCustomizado.helpers";

interface DrawingManagerProps {
  options?: DrawingManagerOptions;
  onPolygonComplete?: (polygon: GooglePolygonLike) => void;
}

function useLatestRef<T>(value: T) {
  const valueRef = useRef(value);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  return valueRef;
}

interface DrawHandlers {
  finishHandler: TerraDrawEventListeners["finish"] | null;
  readyHandler: TerraDrawEventListeners["ready"] | null;
}

function removeMapsListener(
  listener: google.maps.MapsEventListener | null,
) {
  if (listener && typeof listener.remove === "function") {
    listener.remove();
  }
}

function createDrawInstance(
  map: google.maps.Map,
  polygonOptions?: google.maps.PolygonOptions,
) {
  return new TerraDraw({
    adapter: new TerraDrawGoogleMapsAdapter({
      map,
      lib: window.google.maps,
      coordinatePrecision: 9,
    }),
    modes: [
      new TerraDrawPolyLineMode({
        styles: mapPolygonStyles(polygonOptions),
      }),
    ],
  });
}

function cleanupDrawInstance(
  draw: TerraDraw | null,
  handlers: DrawHandlers,
) {
  if (!draw) {
    return;
  }

  if (handlers.finishHandler) {
    draw.off("finish", handlers.finishHandler);
  }

  if (handlers.readyHandler) {
    draw.off("ready", handlers.readyHandler);
  }

  draw.stop();
}

function toFinishedFeatures(
  draw: TerraDraw,
  featureId: Parameters<TerraDrawEventListeners["finish"]>[0],
) {
  const finishedIds: DrawSnapshotFeatureId[] = Array.isArray(featureId)
    ? featureId
    : [featureId];

  return finishedIds.reduce<Array<GooglePolygonLike>>((acc, id) => {
    const feature = draw.getSnapshotFeature(id);

    if (!feature || !isValidFinishedGeometry(feature.geometry)) {
      return acc;
    }

    acc.push(createSyntheticPolygon(id, feature.geometry));
    return acc;
  }, []);
}

function shouldWaitForProjection(map: google.maps.Map) {
  return !(typeof map.getProjection === "function" && map.getProjection());
}

function DrawingManager({
  options = {},
  onPolygonComplete,
}: DrawingManagerProps) {
  const map = useMap();
  const drawRef = useRef<TerraDraw | null>(null);
  const onPolygonCompleteRef = useLatestRef(onPolygonComplete);
  const optionsRef = useLatestRef(options);

  useEffect(() => {
    if (
      !map ||
      typeof window === "undefined" ||
      !window.google ||
      !window.google.maps ||
      drawRef.current
    ) {
      return undefined;
    }

    let cancelled = false;
    let projectionListener: google.maps.MapsEventListener | null = null;
    let handlers: DrawHandlers = {
      finishHandler: null,
      readyHandler: null,
    };
    let drawInstance: TerraDraw | null = null;

    const startTerraDraw = () => {
      if (
        cancelled ||
        drawRef.current ||
        typeof TerraDrawGoogleMapsAdapter !== "function"
      ) {
        return;
      }

      try {
        drawInstance = createDrawInstance(map, optionsRef.current.polygonOptions);
      } catch (err) {
        console.error("[DrawingManager] TerraDraw construction failed:", err);
        return;
      }

      handlers.finishHandler = (featureId) => {
        if (!drawInstance) {
          return;
        }

        const finishedFeatures = toFinishedFeatures(drawInstance, featureId);

        if (finishedFeatures.length > 0) {
          try {
            drawInstance.clear();
          } catch (err) {
            console.warn("[DrawingManager] TerraDraw clear failed:", err);
          }

          finishedFeatures.forEach((finishedFeature) => {
            onPolygonCompleteRef.current?.(finishedFeature);
          });
        }
      };

      handlers.readyHandler = () => {
        if (cancelled || !drawInstance) return;

        try {
          drawInstance.setMode("polyline");
          drawRef.current = drawInstance;
        } catch (err) {
          console.warn("TerraDraw setMode failed:", err);
        }
      };

      drawInstance.on("finish", handlers.finishHandler);
      drawInstance.on("ready", handlers.readyHandler);
      drawInstance.start();
    };

    if (!shouldWaitForProjection(map)) {
      startTerraDraw();
    } else {
      projectionListener = map.addListener("projection_changed", () => {
        if (shouldWaitForProjection(map)) {
          return;
        }

        removeMapsListener(projectionListener);
        projectionListener = null;

        startTerraDraw();
      });
    }

    return () => {
      cancelled = true;

      removeMapsListener(projectionListener);
      projectionListener = null;

      const activeDraw = drawRef.current || drawInstance;
      cleanupDrawInstance(activeDraw, handlers);
      drawRef.current = null;
      drawInstance = null;
      handlers = {
        finishHandler: null,
        readyHandler: null,
      };
    };
  }, [map]);

  useEffect(() => {
    if (drawRef.current) {
      drawRef.current.setModeStyles(
        "polyline",
        mapPolygonStyles(options.polygonOptions),
      );
    }
  }, [options]);

  return null;
}

export default DrawingManager;
